import React, { useContext, useMemo } from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import { usePageTitle, useCompanyBuildings, useCompanies } from "@/hooks";
import { useLocation, useNavigate, useParams } from "react-router";
import { ActionMenu, TableView1, BackButton } from "@/components";
import { Button } from "@mui/material";

export const CompanyLocations = (_props: any) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    companyId: parmsCompanyId = "",
    customerId: paramsCustomerId = "",
    childCompanyId: paramsChildCompanyId = "",
  } = useParams();

  const customerId =
    paramsCustomerId || parmsCompanyId || user?.currentTeamId || "";
  const subsidiaryId = paramsChildCompanyId || user?.currentSubsidiaryId || "";
  const targetCompanyId = subsidiaryId || customerId;

  const { customers } = useCompanies({
    customerId: targetCompanyId,
  });

  const variables = {
    customerId,
    subsidiaryId,
  };

  const { buildings, loading: loadingBuildings } =
    useCompanyBuildings(variables);

  // Set page title
  usePageTitle(gettext("Company Locations"));

  const companyName = useMemo(() => {
    if (location.state?.companyName) {
      return location.state.companyName;
    }
    if (customers && customers.length > 0) {
      const targetCompany = customers.find(
        (c: any) => c.id === targetCompanyId,
      );
      if (targetCompany?.name) {
        return targetCompany.name;
      }
    }
    return targetCompanyId ? `Company ${targetCompanyId}` : "";
  }, [targetCompanyId, location.state?.companyName, customers]);

  const title = gettext("Locations for company: ") + companyName;

  interface BuildingRow {
    address?: string;
    electricity?: unknown[];
    heating?: unknown[];
    water?: unknown[];
    gas?: unknown[];
    energy?: unknown[];
    [key: string]: unknown;
  }

  const menuClick = (action: string, row: Record<string, any>) => {
    const basePath = location.pathname;

    const navigateToMeters = (filter: string) => {
      navigate(`${basePath}/meters/${filter}`, {
        state: {
          filter,
          companyName,
          locationAddress: row?.address,
          parentPath: location.pathname,
        },
      });
    };

    switch (action) {
      case "location-electricity":
        navigateToMeters("electricity");
        break;
      case "location-heating":
        navigateToMeters("heating");
        break;
      case "location-water":
        navigateToMeters("water");
        break;
      case "location-gas":
        navigateToMeters("gas");
        break;
      case "location-energy":
        navigateToMeters("energy");
        break;
      default:
        break;
    }
  };

  const basePath = location.pathname;

  const columns = [
    {
      field: "address",
      headerName: gettext("Address"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    {
      field: "country",
      headerName: gettext("Country"),
      headerAlign: "left",
      type: "string",
      minWidth: 150,
    },
    {
      field: "electricityCount",
      headerName: gettext("Electricity"),
      maxWidth: 100,
      headerAlign: "center",
      type: "number",
      align: "center",
      renderCell: (params: any) => (
        <Button
          variant="outlined"
          onClick={() =>
            navigate(`${basePath}/meters/electricity`, {
              state: {
                filter: "electricity",
                companyName,
                locationAddress: params.row.address,
                parentPath: location.pathname,
              },
            })
          }
        >
          {params.row.electricity?.length || 0}
        </Button>
      ),
    },
    // {
    //   field: "heatingCount",
    //   headerName: gettext("Heating"),
    //   maxWidth: 100,
    //   headerAlign: "center",
    //   type: "number",
    //   align: "center",
    //   renderCell: (params: { row: BuildingRow }) => (
    //     <Button
    //       variant="outlined"
    //       onClick={() =>
    //         navigate(`${basePath}/meters/heating`, {
    //           state: {
    //             filter: "heating",
    //             companyName,
    //             locationAddress: params.row.address,
    //             parentPath: location.pathname,
    //           },
    //         })
    //       }
    //     >
    //       {params.row.heating?.length || 0}
    //     </Button>
    //   ),
    // },
    // {
    //   field: "waterCount",
    //   headerName: gettext("Water"),
    //   maxWidth: 100,
    //   headerAlign: "center",
    //   type: "number",
    //   align: "center",
    //   renderCell: (params: { row: BuildingRow }) => (
    //     <Button
    //       variant="outlined"
    //       onClick={() =>
    //         navigate(`${basePath}/meters/water`, {
    //           state: {
    //             filter: "water",
    //             companyName,
    //             locationAddress: params.row.address,
    //             parentPath: location.pathname,
    //           },
    //         })
    //       }
    //     >
    //       {params.row.water?.length || 0}
    //     </Button>
    //   ),
    // },
    {
      field: "gasCount",
      headerName: gettext("Natural Gas"),
      maxWidth: 100,
      headerAlign: "center",
      type: "number",
      align: "center",
      renderCell: (params: { row: BuildingRow }) => (
        <Button
          variant="outlined"
          onClick={() =>
            navigate(`${basePath}/meters/gas`, {
              state: {
                filter: "gas",
                companyName,
                locationAddress: params.row.address,
                parentPath: location.pathname,
              },
            })
          }
        >
          {params.row.gas?.length || 0}
        </Button>
      ),
    },
    // {
    //   field: "energyCount",
    //   headerName: gettext("Energy"),
    //   maxWidth: 100,
    //   headerAlign: "center",
    //   type: "number",
    //   align: "center",
    //   renderCell: (params: { row: BuildingRow }) => (
    //     <Button
    //       variant="outlined"
    //       onClick={() =>
    //         navigate(`${basePath}/meters/energy`, {
    //           state: {
    //             filter: "energy",
    //             companyName,
    //             locationAddress: params.row.address,
    //             parentPath: location.pathname,
    //           },
    //         })
    //       }
    //     >
    //       {params.row.energy?.length || 0}
    //     </Button>
    //   ),
    // },
    {
      field: "actions",
      type: "actions",
      headerName: gettext("Actions"),
      hideable: false,
      maxWidth: 75,
      align: "right",
      headerAlign: "right",
      renderCell: (params: { row: BuildingRow }) => (
        <ActionMenu menuFor="location" row={params.row} menuClick={menuClick} />
      ),
    },
  ];

  // We fake an 'id' since DataGrid expects an id. If building has no true id, we use address.
  const buildingsWithIds = useMemo(() => {
    if (!buildings) return [];
    return buildings.map((b: BuildingRow, index: number) => {
      const address = b.address;
      return {
        id: address || String(index),
        ...b,
      };
    });
  }, [buildings]);

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
  };

  const backLink = location.state?.parentPath;

  return (
    <>
      <TableView1
        title={title}
        rows={buildingsWithIds}
        columns={columns as any}
        loading={loadingBuildings}
        initialState={initialState}
        postToolbar={<>{!!backLink && <BackButton link={`${backLink}`} />}</>}
        showGridToolbar
        showGridToolbarQuickFilter
        // beforeTable={
        //   <>
        //     <br />
        //   </>
        // }
      />
    </>
  );
};
